import { CATEGORIES, CATEGORY_BY_ID } from './categories.js';
import { slugify, stableHash } from '../utils/ids.js';
import { getHouseDesignProfile } from './houseDesignProfiles.js';

const STYLE_POOLS = {
  residential: ['Tropical Urban', 'Scandinavian', 'Modern Compact', 'Mediterranean', 'Japandi', 'Sundanese Contemporary', 'Javanese Courtyard', 'Industrial Warm', 'Bali Garden', 'Eco Modular'],
  religious: ['Serene Courtyard', 'Contemporary Hall', 'Heritage Inspired', 'Community Pavilion', 'Light Filled', 'Garden Sanctuary', 'Urban Compact', 'Grand Symmetry', 'Tropical Calm', 'Minimal Reverent'],
  hospitality: ['Boutique Modern', 'Tropical Retreat', 'Urban Compact', 'Family Comfort', 'Courtyard Living', 'Business Classic', 'Scandinavian Calm', 'Garden Stay', 'Contemporary Suite', 'Efficient Modular'],
  education: ['Open Learning', 'Courtyard Campus', 'Compact Urban', 'Tropical School', 'Creative Classroom', 'Community Learning', 'Modern Academic', 'Green Campus', 'Flexible Lab', 'Library Focus'],
  food: ['Warm Industrial', 'Tropical Fresh', 'Compact Express', 'Family Friendly', 'Modern Street', 'Garden Dining', 'Minimal Artisan', 'Bright Casual', 'Urban Counter', 'Natural Wood'],
  retail: ['Bright Retail', 'Modern Display', 'Compact Corner', 'Open Front', 'Warm Local', 'Grid Merchandising', 'Premium Neutral', 'Tropical Shop', 'Urban Efficient', 'Flexible Market'],
  office: ['Agile Office', 'Executive Calm', 'Creative Hub', 'Compact Professional', 'Biophilic Work', 'Open Collaboration', 'Client Focus', 'Hybrid Studio', 'Warm Minimal', 'Tropical Office'],
  creative: ['Production Ready', 'Acoustic Calm', 'Bright Workshop', 'Flexible Stage', 'Creator Compact', 'Gallery White', 'Dark Studio', 'Natural Light', 'Modular Creative', 'Community Maker'],
  service: ['Clean Service', 'Express Compact', 'Welcoming Front', 'Professional Calm', 'Efficient Flow', 'Bright Practical', 'Premium Local', 'Urban Service', 'Accessible Layout', 'Modular Counter'],
  commercial: ['Flexible Commercial', 'Wide Frontage', 'Compact Utility', 'Modern Shell', 'Two Level Hybrid', 'Courtyard Business', 'Urban Modular', 'Clean Industrial', 'Community Hall', 'Open Span'],
  community: ['Inclusive Hall', 'Flexible Gathering', 'Neighborhood Hub', 'Open Pavilion', 'Creative Community', 'Tropical Assembly', 'Compact Center', 'Courtyard Social', 'Modern Multipurpose', 'Accessible Community']
};

const ARCHETYPES = ['Compact', 'Balanced', 'Spacious', 'Corner', 'Courtyard'];
const CACHE_LIMIT = 4;

function buildTemplate(category, index) {
  const styles = STYLE_POOLS[category.family] ?? STYLE_POOLS.commercial;
  const routeStyle = styles[index % styles.length];
  const archetype = ARCHETYPES[Math.floor(index / styles.length) % ARCHETYPES.length];
  const serial = String(index + 1).padStart(3, '0');
  const seed = stableHash(`${category.id}:${index}`);
  const floorCap = ['house', 'villa', 'hospitality', 'commercial', 'office', 'education'].includes(category.generatorType) ? 3 : 2;
  const generatedFloors = 1 + ((seed >>> 3) % floorCap);
  const width = 8 + (seed % 7) + (archetype === 'Spacious' ? 4 : 0);
  const depth = 7 + ((seed >>> 5) % 7) + (archetype === 'Courtyard' ? 3 : 0);
  const houseProfile = category.id === 'house' ? getHouseDesignProfile(index, seed) : null;
  const style = houseProfile?.label ?? routeStyle;
  const floors = houseProfile?.floors ?? generatedFloors;
  const id = `${category.id}-${slugify(routeStyle)}-${slugify(archetype)}-${serial}`;
  return Object.freeze({
    id,
    categoryId: category.id,
    name: `${category.labels.en} ${style} ${archetype} ${index + 1}`,
    style,
    archetype,
    floors,
    footprint: { width, depth },
    previewSeed: seed,
    generatorType: category.generatorType,
    defaultObjects: category.generatorType,
    defaultMaterials: houseProfile
      ? [houseProfile.wall, 'floor-oak', houseProfile.roof]
      : ['wall-warm', 'floor-oak', seed % 2 ? 'roof-slate' : 'roof-terracotta'],
    tags: [category.family, style.toLowerCase(), archetype.toLowerCase(), houseProfile?.id ?? '', floors === 1 ? 'lightweight' : 'multi-floor'].filter(Boolean),
    difficulty: floors === 1 ? 'Beginner' : floors === 2 ? 'Intermediate' : 'Advanced',
    estimatedArea: width * depth * floors,
    description: houseProfile
      ? `${style} house inspired by Indonesian tropical, heritage, and contemporary massing with a ${archetype.toLowerCase()} editable plan.`
      : `${style} ${category.labels.en.toLowerCase()} with a ${archetype.toLowerCase()} plan, procedural structure, and editable interior objects.`,
    ...(houseProfile ? { houseProfile } : {})
  });
}

class TemplateRegistry {
  #cache = new Map();

  getCategories() {
    return CATEGORIES;
  }

  generateTemplatesForCategory(categoryId, count = 50) {
    const category = CATEGORY_BY_ID.get(categoryId);
    if (!category) return [];
    const safeCount = Math.max(1, Math.min(50, Number(count) || 50));
    return Array.from({ length: safeCount }, (_, index) => buildTemplate(category, index));
  }

  getTemplatesByCategory(categoryId) {
    if (this.#cache.has(categoryId)) {
      const value = this.#cache.get(categoryId);
      this.#cache.delete(categoryId);
      this.#cache.set(categoryId, value);
      return value;
    }
    const templates = this.generateTemplatesForCategory(categoryId, 50);
    if (!templates.length) return templates;
    this.#cache.set(categoryId, templates);
    while (this.#cache.size > CACHE_LIMIT) this.#cache.delete(this.#cache.keys().next().value);
    return templates;
  }

  getTemplate(categoryId, templateId) {
    return this.getTemplatesByCategory(categoryId).find((item) => item.id === templateId) ?? null;
  }

  validateTemplate(template) {
    const required = ['id', 'categoryId', 'name', 'style', 'floors', 'footprint', 'previewSeed', 'generatorType', 'defaultObjects', 'defaultMaterials', 'tags', 'difficulty', 'estimatedArea', 'description'];
    return Boolean(template && required.every((key) => template[key] !== undefined) && CATEGORY_BY_ID.has(template.categoryId) && template.footprint.width > 0 && template.footprint.depth > 0);
  }
}

export const templateRegistry = new TemplateRegistry();
export { TemplateRegistry };
